import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  :root {
    /* Automotive Color Palette - Premium Racing Theme */
    --racing-red: #DC143C;
    --racing-red-light: #FF6B8A;
    --racing-red-dark: #B91C3C;
    
    --carbon-black: #0A0A0A;
    --carbon-gray: #1A1A1A;
    --steel-gray: #2D2D2D;
    --chrome-silver: #C0C0C0;
    --platinum: #E5E5E5;
    
    --electric-blue: #00D4FF;
    --electric-blue-light: #4DE4FF;
    --electric-blue-dark: #0099CC;
    
    --turbo-orange: #FF6B35;
    --turbo-orange-light: #FF8A5C;
    --turbo-orange-dark: #E5522A;
    
    --neon-green: #00FF88;
    --neon-green-light: #33FFAA;
    --neon-green-dark: #00CC6A;
    
    /* Neutral Colors */
    --white: #FFFFFF;
    --off-white: #FAFAFA;
    --light-gray: #F5F5F5;
    --medium-gray: #9CA3AF;
    --dark-gray: #374151;
    
    /* Gradients */
    --racing-gradient: linear-gradient(135deg, var(--racing-red) 0%, var(--racing-red-dark) 100%);
    --carbon-gradient: linear-gradient(135deg, var(--carbon-black) 0%, var(--steel-gray) 100%);
    --electric-gradient: linear-gradient(135deg, var(--electric-blue) 0%, var(--electric-blue-dark) 100%);
    --chrome-gradient: linear-gradient(135deg, var(--chrome-silver) 0%, var(--platinum) 100%);
    
    /* Spacing Scale */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-10: 2.5rem;
    --space-12: 3rem;
    --space-16: 4rem;
    --space-20: 5rem;
    --space-24: 6rem;
    
    /* Border Radius */
    --radius-sm: 0.25rem;
    --radius-md: 0.5rem;
    --radius-lg: 0.75rem;
    --radius-xl: 1rem;
    --radius-2xl: 1.5rem;
    --radius-full: 9999px;
    
    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    --shadow-racing: 0 25px 50px -12px rgba(220, 20, 60, 0.25);
    --shadow-carbon: 0 25px 50px -12px rgba(10, 10, 10, 0.4);
    
    /* Typography Scale */
    --text-xs: 0.75rem;
    --text-sm: 0.875rem;
    --text-base: 1rem;
    --text-lg: 1.125rem;
    --text-xl: 1.25rem;
    --text-2xl: 1.5rem;
    --text-3xl: 1.875rem;
    --text-4xl: 2.25rem;
    --text-5xl: 3rem;
    --text-6xl: 3.75rem;
    
    /* Line Heights */
    --leading-tight: 1.25;
    --leading-snug: 1.375;
    --leading-normal: 1.5;
    --leading-relaxed: 1.625;
    --leading-loose: 2;
    
    /* Font Weights */
    --font-light: 300;
    --font-normal: 400;
    --font-medium: 500;
    --font-semibold: 600;
    --font-bold: 700;
    --font-extrabold: 800;
    --font-black: 900;
    
    /* Transitions */
    --transition-fast: 150ms ease-in-out;
    --transition-normal: 300ms ease-in-out;
    --transition-slow: 500ms ease-in-out;
    
    /* Z-Index Scale */
    --z-dropdown: 1000;
    --z-sticky: 1020;
    --z-fixed: 1030;
    --z-modal: 1040;
    --z-popover: 1050;
    --z-tooltip: 1060;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 
                 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 
                 'Helvetica Neue', sans-serif;
    font-size: var(--text-base);
    font-weight: var(--font-normal);
    line-height: var(--leading-normal);
    color: var(--carbon-black);
    background-color: var(--off-white);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }

  /* Typography Elements */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Rajdhani', 'Inter', sans-serif;
    font-weight: var(--font-bold);
    line-height: var(--leading-tight);
    color: var(--carbon-black);
    margin-bottom: var(--space-4);
  }

  h1 {
    font-size: var(--text-5xl);
    font-weight: var(--font-black);
    letter-spacing: -0.025em;
  }

  h2 {
    font-size: var(--text-4xl);
    font-weight: var(--font-extrabold);
  }

  h3 {
    font-size: var(--text-3xl);
    font-weight: var(--font-bold);
  }

  h4 {
    font-size: var(--text-2xl);
    font-weight: var(--font-semibold);
  }

  h5 {
    font-size: var(--text-xl);
    font-weight: var(--font-medium);
  }

  h6 {
    font-size: var(--text-lg);
    font-weight: var(--font-medium);
  }

  p {
    margin-bottom: var(--space-4);
    line-height: var(--leading-relaxed);
    color: var(--dark-gray);
  }

  a {
    color: var(--racing-red);
    text-decoration: none;
    transition: color var(--transition-fast);
    
    &:hover {
      color: var(--racing-red-dark);
      text-decoration: underline;
    }
  }

  /* Utility Classes */
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 var(--space-6);
  }

  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }

  .font-light { font-weight: var(--font-light); }
  .font-normal { font-weight: var(--font-normal); }
  .font-medium { font-weight: var(--font-medium); }
  .font-semibold { font-weight: var(--font-semibold); }
  .font-bold { font-weight: var(--font-bold); }
  .font-extrabold { font-weight: var(--font-extrabold); }
  .font-black { font-weight: var(--font-black); }

  .text-racing { color: var(--racing-red); }
  .text-electric { color: var(--electric-blue); }
  .text-turbo { color: var(--turbo-orange); }
  .text-neon { color: var(--neon-green); }
  .text-carbon { color: var(--carbon-black); }
  .text-chrome { color: var(--chrome-silver); }

  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--light-gray);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--racing-gradient);
    border-radius: var(--radius-full);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--racing-red-dark);
  }

  /* Selection Styling */
  ::selection {
    background: var(--racing-red);
    color: var(--white);
  }

  ::-moz-selection {
    background: var(--racing-red);
    color: var(--white);
  }

  /* Focus Styles */
  :focus {
    outline: 2px solid var(--racing-red);
    outline-offset: 2px;
  }

  :focus:not(:focus-visible) {
    outline: none;
  }

  /* Button Reset */
  button {
    border: none;
    background: none;
    font: inherit;
    cursor: pointer;
    outline: inherit;
  }

  /* List Reset */
  ul, ol {
    list-style: none;
  }

  /* Image Defaults */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Form Elements */
  input, textarea, select {
    font: inherit;
    color: inherit;
  }

  /* Responsive Breakpoints */
  @media (max-width: 640px) {
    .container {
      padding: 0 var(--space-4);
    }
    
    h1 { font-size: var(--text-3xl); }
    h2 { font-size: var(--text-2xl); }
    h3 { font-size: var(--text-xl); }
  }

  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }
  }
`;
